import { FunctionalComponent } from 'preact';
import { useContext, useEffect, useState } from 'preact/compat';
import { Checkbox } from '../Checkbox';
import { Question } from '../Questions';
import { API_URL } from '../../constants';
import { cn, requestOptions } from '../../utilities';
import { WidgetContext } from '../../widget.context';
import { Icon } from '../Icon/icon';
import './style.css';

export interface ICommentsProps {
  class?: string;
}

export enum EQuestionType {
  SA = 'SA_QUESTION',
  TT = 'TOPIC_TEST_QUESTION',
}

export enum EQuestionTypeDisplay {
  SA_QUESTION = 'SA',
  TOPIC_TEST_QUESTION = 'TT',
}

export interface IStudent {
  id: number;
  schoolId: number;
  email: string;
  firstName: string;
  lastName: string;
  studyYear: number;
}

export interface IComment {
  id: number;
  order: number;
  questionType: EQuestionType;
  question: string;
  mentionedStudents: IStudent[];
}

export const QuestionContainer: FunctionalComponent<ICommentsProps> = (props) => {
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(-1);
  const [deleteTimer, setDeleteTimer] = useState<number>(-1);
  const [draggable, setDraggable] = useState(-1);
  const [dragged, setDragged] = useState(-1);
  const [questionsList, setQuestionsList] = useState<IComment[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggle = () => setIsCollapsed(!isCollapsed);

  const onDeleteClick = (questionId: number) => {
    if (deleteTimer) {
      clearTimeout(deleteTimer);
      setDeleteTimer(-1);
    }

    setDeleteConfirmationId(questionId);
    setDeleteTimer(setTimeout(() => setDeleteConfirmationId(-1), 2000) as unknown as number); // Casting to number to avoid NodeJS.Timeout type
  };

  const onDeleteConfirm = () => {
    if (!deleteTimer) {
      return;
    }

    fetch(
      `${API_URL}/tutorial-session/questions/${deleteConfirmationId}`,
      requestOptions(rootData?.token, 'delete'),
    ).then(() => fetchQuestions(rootData?.ctId, rootData!.token!));

    setDeleteConfirmationId(-1);
    clearTimeout(deleteTimer);
    setDeleteTimer(-1);
  };

  const [isNamesHidden, setIsNamesHidden] = useState(false);
  const toggleNames = (event: Event) => setIsNamesHidden((event.target as HTMLInputElement).checked);

  const fetchQuestions = (ctId: any, token: string) => {
    fetch(`${API_URL}/tutorial-session/topic-test/${ctId}/questions`, requestOptions(token))
      .then((res) => res.json())
      .then((res) => setQuestionsList(res.data ? res.data.items : []));
  };

  const { rootData, session } = useContext(WidgetContext);
  useEffect(() => {
    if (!rootData || !rootData.token || !session || !session.tutorialSessionPlanId) {
      return;
    }

    fetchQuestions(rootData?.ctId, rootData.token);
  }, [rootData, session]);

  const onMouseDown = (idx: number) => setDraggable(idx);
  const onMouseUp = () => setDraggable(-1);

  const dragStart = (idx: number) => setDragged(idx);
  const dragEnd = () => {
    setDraggable(-1);
    setDragged(-1);
  };
  const dragOver = (idx: number, e: DragEvent) => {
    if (idx === dragged) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };
  const drop = (target: number) => {
    if (dragged === target) {
      return;
    }

    const targetIdx = questionsList.findIndex((item) => item.id === target);
    const newPosition = questionsList[targetIdx].order;
    fetch(
      `${API_URL}/tutorial-session/questions/${dragged}/change-position/${newPosition}`,
      requestOptions(rootData?.token, 'POST'),
    ).then(() => fetchQuestions(rootData?.ctId, rootData!.token!));
  };

  const sortedQuestions = [...questionsList].sort((a, b) => a.order - b.order);

  return (
    <div class={cn(props.class, 'cw-container')}>
      <div class="cw-container__header">
        <h6>All questions</h6>
        <Icon onClick={toggle} icon="chevron-down" />
      </div>
      <div class={cn('cw-container__body', isCollapsed ? 'cw-container__body--collapsed' : '')}>
        <Checkbox label="Hide student names" onCheck={toggleNames} />
        {sortedQuestions.map((item) => (
          <div key={`${item.id}_${item.order}`} class="cw-question__wrapper">
            <Question
              question={item}
              isUsersHidden={isNamesHidden}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              onDragStart={dragStart}
              onDragEnd={dragEnd}
              onDragOver={dragOver}
              onDrop={drop}
              draggable={draggable === item.id}
              onDeleteClick={onDeleteClick}
            />

            {deleteConfirmationId === item.id && (
              <div class="cw-question__delete-confirmation">
                <div>Are you sure to delete this question?</div>
                <div class="cw-question__confirm" onClick={onDeleteConfirm}>
                  Yes, I'm completely sure!
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
